import { isEmpty, keys } from 'lodash-es';
import { IBusinessUnit, ICandidate, IJob, IRelocationPreference } from 'wisestep-api';
import {
  willingToTravelList,
  noticePeriodInDaysList,
  authorizationCategoryList,
  currentJobTypeList,
  usVisaIndependentList,
  usVisaDependentList,
  germanyVisaIndependentList,
  germanyVisaDependentList,
  ukVisaIndependentList,
  ukVisaDependentList,
  polandVisaIndependentList,
} from 'wisestep-base';

const getNoticePeriodInDays = (day: number | null) => {
  let correctDay = { index: -1, key: '' };

  if (day !== null) {
    switch (day) {
      case 0:
        correctDay = {
          index: noticePeriodInDaysList['Immediately'],
          key: 'Immediately',
        };
        break;
      case 30:
        correctDay = {
          index: noticePeriodInDaysList['30 Days'],
          key: '30 Days',
        };
        break;
      case 45:
        correctDay = {
          index: noticePeriodInDaysList['45 Days'],
          key: '45 Days',
        };
        break;
      case 60:
        correctDay = {
          index: noticePeriodInDaysList['60 Days'],
          key: '60 Days',
        };
        break;
      case 90:
        correctDay = {
          index: noticePeriodInDaysList['90 Days'],
          key: '90 Days',
        };
        break;
    }
    return correctDay.index != -1 ? correctDay : null;
  }
  return null;
};

const getSubmissionNoticePeriodInDays = (day: number | string | null) => {
  if (day === null || day === undefined) return null;
  const noticePeriodMap: Record<string | number, { index: number; key: string }> = {
    0: { index: noticePeriodInDaysList['Immediately'], key: 'Immediately' },
    Immediate: { index: noticePeriodInDaysList['Immediately'], key: 'Immediately' },
    Within_15_Days: { index: noticePeriodInDaysList['15 Days'], key: '15 Days' },
    15: { index: noticePeriodInDaysList['15 Days'], key: '15 Days' },
    30: { index: noticePeriodInDaysList['30 Days'], key: '30 Days' },
    Within_1_Month: { index: noticePeriodInDaysList['30 Days'], key: '30 Days' },
    45: { index: noticePeriodInDaysList['45 Days'], key: '45 Days' },
    60: { index: noticePeriodInDaysList['60 Days'], key: '60 Days' },
    Within_2_Months: { index: noticePeriodInDaysList['60 Days'], key: '60 Days' },
    90: { index: noticePeriodInDaysList['90 Days'], key: '90 Days' },
    Within_3_Months: { index: noticePeriodInDaysList['90 Days'], key: '90 Days' },
  };
  return noticePeriodMap[day] ?? null;
};

const setAuthorizationCategory = (workAuth: string | null) => {
  let correctWorkAuth = { index: -1, key: '' };

  if (workAuth !== null) {
    switch (workAuth) {
      case 'Citizen':
        correctWorkAuth = {
          index: authorizationCategoryList['Citizen'],
          key: 'Citizen',
        };
        break;
      case 'Visa Independent':
        correctWorkAuth = {
          index: authorizationCategoryList['Visa Independent'],
          key: 'Visa Independent',
        };
        break;
      case 'Visa Dependent':
        correctWorkAuth = {
          index: noticePeriodInDaysList['Visa Dependent'],
          key: 'Visa Dependent',
        };
        break;
      case 'Others':
        correctWorkAuth = {
          index: noticePeriodInDaysList['Others'],
          key: 'Others',
        };
        break;
    }
    return correctWorkAuth.index != -1 ? correctWorkAuth : null;
  }
  return null;
};

const getComPref = (candidate: ICandidate) => {
  const prefIndex: number[] = [];
  const prefKey: string[] = [];
  if (candidate.commPreferenceEmail) {
    prefIndex.push(0);
    prefKey.push('Email');
  }
  if (candidate.commPreferencePhone) {
    prefIndex.push(1);
    prefKey.push('Phone');
  }
  return {
    index: prefIndex,
    key: prefKey,
  };
};

const getJobPref = (candidate: ICandidate) => {
  const prefIndex: number[] = [];
  const prefKey: string[] = [];
  if (candidate.jobPreferencePermanent) {
    prefIndex.push(0);
    prefKey.push('Permanent');
  }
  if (candidate.jobPreferenceContract) {
    prefIndex.push(1);
    prefKey.push('Contract');
  }
  return {
    index: prefIndex,
    key: prefKey,
  };
};

export const getRelocationPreference = (relocationPreference: string) => {
  let relocation;
  if (relocationPreference) {
    switch (relocationPreference) {
      case IRelocationPreference.WITHIN_STATE:
        relocation = 0;
        break;
      case IRelocationPreference.WITHIN_COUNTRY:
        relocation = 1;
        break;
      case IRelocationPreference.ANYWHERE:
        relocation = 2;
        break;
      default:
        relocation = -1;
        break;
    }
  }
  return relocation;
};

export const getAllAlternateNumbers = (data: string[]) => {
  if (!isEmpty(data) && data.includes('false')) {
    return data.filter((val) => val != 'false');
  }
  return isEmpty(data) ? [] : data;
};

export const getRawPhoneNumber = (phNo: string) => phNo.replace(/\D/g, '');

const setAuthorization = (data: string | null | undefined) => {
  if (data) {
    if (keys(usVisaIndependentList).includes(data)) {
      return {
        index: usVisaIndependentList[data],
        key: data,
      };
    } else if (keys(usVisaDependentList).includes(data)) {
      return {
        index: usVisaDependentList[data],
        key: data,
      };
    } else if (keys(germanyVisaIndependentList).includes(data)) {
      return {
        index: germanyVisaIndependentList[data],
        key: data,
      };
    } else if (keys(germanyVisaDependentList).includes(data)) {
      return {
        index: germanyVisaDependentList[data],
        key: data,
      };
    } else if (keys(ukVisaIndependentList).includes(data)) {
      return {
        index: ukVisaIndependentList[data],
        key: data,
      };
    } else if (keys(ukVisaDependentList).includes(data)) {
      return {
        index: ukVisaDependentList[data],
        key: data,
      };
    } else if (keys(polandVisaIndependentList).includes(data)) {
      return {
        index: polandVisaIndependentList[data],
        key: data,
      };
    }
  }
};

export const mappingFormDefaultValues = (candidate: ICandidate) => {
  return {
    availableFrom: candidate?.availableFrom ?? null,
    currentLocation: candidate.currentLocation ? [candidate.currentLocation] : [],
    currentSalary: {
      currency: candidate.currentSalaryCurrency ?? null,
      value: candidate.currentSalary ?? 0,
      timeSpan: candidate.currentSalaryTimeSpan ?? null,
    },
    expectedSalary: {
      currency: candidate.expectedSalaryCurrency ?? null,
      value: candidate.expectedBaseSalary ?? 0,
      timeSpan: candidate.expectedBaseSalaryTimeSpan ?? null,
    },
    otherAllowance: candidate.otherCurrentAllowances ?? '',
    expectedAllowance: candidate.otherExpectedAllowances ?? '',
    willingToTravel:
      candidate.willingToRelocate != null
        ? candidate.willingToRelocate
          ? willingToTravelList['Yes']
          : willingToTravelList['No']
        : null,
    familyWillingToRelocate:
      candidate.familyWillingToRelocate != null
        ? candidate.familyWillingToRelocate
          ? { index: willingToTravelList['Yes'], key: 'Yes' }
          : { index: willingToTravelList['No'], key: 'No' }
        : null,
    noticePeriodInDays: getNoticePeriodInDays(candidate.noticePeriodInDays),
    authorizationCategory: candidate.workAuthorizationCategory
      ? setAuthorizationCategory(candidate.workAuthorizationCategory)
      : null,
    authorization:
      candidate.workAuthorizationCategory &&
      candidate.workAuthorizationCategory != 'Others' &&
      candidate.workAuthorization
        ? setAuthorization(candidate.workAuthorization)
        : null,
    workAuthorization:
      candidate.workAuthorizationCategory &&
      candidate.workAuthorizationCategory == 'Others' &&
      candidate.workAuthorization
        ? candidate.workAuthorization
        : null,
    nationality: candidate.nationality ?? '',
    tags: candidate.tags ?? [],
    source: candidate.source ?? '',
    candidateId: candidate.candidateId ?? '',
    communicationPreference: getComPref(candidate),
    jobPreference: getJobPref(candidate),
    currentJobType: candidate.currentJobType
      ? { index: currentJobTypeList[candidate.currentJobType], key: candidate.currentJobType }
      : null,
    expInYears: candidate.totalYearsExperience && candidate.totalYearsExperience,
    expInMonth: candidate.totalMonthsExperience && candidate.totalMonthsExperience,
    relExpInYears: candidate.relevantYearsExperience && candidate.relevantYearsExperience,
    relExpInMonth: candidate.relevantMonthsExperience && candidate.relevantMonthsExperience,
    jobTitle: candidate.currentJobTitle ?? '',
    currentCompany: candidate.currentEmployer ?? '',
    prevJobTitles: candidate.previousJobTitles ?? [],
    prevEmployers: candidate.previousEmployers ?? [],
    citizenship: candidate.citizenship ?? '',
    preferredLocation: candidate.preferredLocations ?? [],
    relocationList:
      candidate.relocationPreference != null
        ? getRelocationPreference(candidate?.relocationPreference)
        : null,
    highestEducation: candidate?.educationQualifications ?? [],
    institutesAttended: candidate?.institutionsAttended ?? [],
    visaType: candidate?.visaType ?? '',
    certifications: candidate?.certifications ?? [],
    candidateLanguages: candidate?.candidateLanguages ?? [],
    passportNumber: candidate.passportNumber ?? '',
    passportType: candidate.passportType ?? '',
    dob: candidate.dob ?? null,
    gender: candidate.gender ?? 'NOT_SPECIFIED',
    age: candidate.age ?? '',
    postalAddress: candidate.postalAddress ?? '',
    ssnNumber: candidate.ssnNumber ?? '',
    panCard: candidate?.panCard ?? '',
    aadharNumber: candidate?.aadharNumber ?? '',
    hasVeteranStatus:
      candidate.hasVeteranStatus != null
        ? candidate.hasVeteranStatus
          ? { index: willingToTravelList['Yes'], key: 'Yes' }
          : { index: willingToTravelList['No'], key: 'No' }
        : null,
    personalWebsite: candidate.personalWebsite ?? '',
    securityClearance:
      candidate.securityAllowance?.status != null
        ? candidate.securityAllowance?.status
          ? { index: willingToTravelList['Yes'], key: 'Yes' }
          : { index: willingToTravelList['No'], key: 'No' }
        : null,
    desiredJobTitles: candidate.desiredJobTitles ?? [],
    willingToTravelType: candidate.willingToTravelType ?? '',
  };
};

export const submissionFormDefaultValues = (
  candidate: ICandidate | null,
  job?: IJob | undefined | null,
  BuContext?: IBusinessUnit[] | null
) => {
  if (candidate) {
    return {
      availableFrom: candidate?.availableFrom ?? null,
      name: candidate.name ?? '',
      email: candidate.email ?? '',
      alternateEmails: candidate?.alternateEmails?.[0] ?? '',
      phone: getRawPhoneNumber(candidate.phoneNumber ?? ''),
      alternatePhoneNumbers: getAllAlternateNumbers(candidate.alternatePhoneNumbers)?.[0] ?? '',
      socialLinks: candidate.profileURLs ?? candidate?.linkedInURL ? [candidate.linkedInURL] : [],
      messengerLinks: candidate.messengers ?? [{ type: '', messengerId: '' }],
      skills: candidate.skills ?? [],
      currentLocation: candidate.currentLocation ? [candidate.currentLocation] : [],
      currentSalary: {
        currency: candidate.currentSalaryCurrency ?? job?.salaryCurrency ?? null,
        value: candidate.currentSalary ?? 0,
        timeSpan: candidate.currentSalaryTimeSpan ?? job?.salaryTimeSpan ?? null,
      },
      expectedSalary: {
        currency: candidate.expectedSalaryCurrency ?? job?.salaryCurrency ?? null,
        value: candidate.expectedBaseSalary ?? 0,
        timeSpan: candidate.expectedBaseSalaryTimeSpan ?? job?.salaryTimeSpan ?? null,
      },
      otherAllowance: candidate.otherCurrentAllowances ?? '',
      expectedAllowance: candidate.otherExpectedAllowances ?? '',
      willingToTravel:
        candidate.willingToRelocate != null
          ? candidate.willingToRelocate
            ? { index: willingToTravelList['Yes'], key: 'Yes' }
            : { index: willingToTravelList['No'], key: 'No' }
          : null,
      noticePeriodInDays: getSubmissionNoticePeriodInDays(candidate.noticePeriod),
      authorizationCategory: candidate.workAuthorizationCategory
        ? setAuthorizationCategory(candidate.workAuthorizationCategory)
        : null,
      authorization:
        candidate.workAuthorizationCategory &&
        candidate.workAuthorizationCategory != 'Others' &&
        candidate.workAuthorization
          ? setAuthorization(candidate.workAuthorization)
          : null,
      workAuthorization:
        candidate.workAuthorizationCategory &&
        candidate.workAuthorizationCategory == 'Others' &&
        candidate.workAuthorization
          ? candidate.workAuthorization
          : null,
      nationality: candidate.nationality ?? '',
      visaStatus: candidate.visaStatus ?? '',
      visaType: candidate?.visaType ?? '',
      visaValidity: candidate.visaValidity ?? '',
      tags: candidate.tags ?? [],
      source: candidate.source ?? '',
      candidatePersonalId: candidate.candidateId ?? candidate.id ?? '',
      candidateId: candidate.candidateId ?? candidate.id ?? '',
      communicationPreference: getComPref(candidate),
      jobPreference: getJobPref(candidate),
      currentJobType: candidate.currentJobType
        ? { index: currentJobTypeList[candidate.currentJobType], key: candidate.currentJobType }
        : null,
      currentJobIncludesTravel:
        candidate.currentJobTravelType === 'None'
          ? { key: 'No', index: willingToTravelList['No'] }
          : { key: 'Yes', index: willingToTravelList['Yes'] },
      currentJobTravelType: candidate.currentJobTravelType,
      expInYears: candidate.totalYearsExperience && candidate.totalYearsExperience,
      expInMonth: candidate.totalMonthsExperience && candidate.totalMonthsExperience,
      relExpInYears: candidate.relevantYearsExperience && candidate.relevantYearsExperience,
      relExpInMonth: candidate.relevantMonthsExperience && candidate.relevantMonthsExperience,
      jobTitle: candidate.currentJobTitle ?? '',
      currentCompany: candidate.currentEmployer ?? '',
      prevJobTitles: candidate.previousJobTitles ?? [],
      prevEmployers: candidate.previousEmployers ?? [],
      citizenship: candidate.citizenship ?? '',
      certifications: candidate?.certifications?.length
        ? candidate.certifications
        : candidate?.candidateCertificationsData?.map((cer) => cer.name).filter(Boolean) || [],
      highestEducation: candidate?.educationQualifications ?? [],
      institutesAttended: candidate?.institutionsAttended?.length
        ? candidate.institutionsAttended
        : candidate?.education?.map((edu) => edu.org).filter(Boolean) || [],
      willingToTravelType: candidate.willingToTravelType ?? '',
      relocationPreferences: candidate.relocationPreference ?? '',
      familyWillingToRelocate:
        candidate.familyWillingToRelocate != null
          ? candidate.familyWillingToRelocate
            ? { index: willingToTravelList['Yes'], key: 'Yes' }
            : { index: willingToTravelList['No'], key: 'No' }
          : null,
      interviewingWithOthers:
        candidate.interviewingWithOthers != null
          ? candidate.interviewingWithOthers
            ? { index: willingToTravelList['Yes'], key: 'Yes' }
            : { index: willingToTravelList['No'], key: 'No' }
          : null,
      rtrConsent: candidate.rtrConsent,
      jobChangeReason: candidate.reasonForNewJob ?? '',
      recruiterAssessment: candidate.assessment ?? '',
      passportNumber: candidate.passportNumber ?? '',
      passportType: candidate.passportType ?? '',
      dob: candidate.dob ? new Date(candidate.dob) : null,
      gender: candidate.gender ?? 'NOT_SPECIFIED',
      age: candidate.age ?? '',
      postalAddress: candidate.postalAddress ?? '',
      ssnNumber: candidate.ssnNumber ?? '',
      panCard: candidate.panCard ?? '',
      aadharNumber: candidate.aadharNumber ?? '',
      buId: getBuId(candidate, BuContext ?? []),
      preferredLocation: candidate.preferredLocations ?? [],
      relocationList:
        candidate.relocationPreference != null
          ? getRelocationPreference(candidate?.relocationPreference)
          : null,
      hasVeteranStatus:
        candidate.hasVeteranStatus != null
          ? candidate.hasVeteranStatus
            ? { index: willingToTravelList['Yes'], key: 'Yes' }
            : { index: willingToTravelList['No'], key: 'No' }
          : null,
      personalWebsite: candidate.personalWebsite ?? '',
      securityClearance:
        candidate.securityAllowance?.status != null
          ? candidate.securityAllowance?.status
            ? { index: willingToTravelList['Yes'], key: 'Yes' }
            : { index: willingToTravelList['No'], key: 'No' }
          : null,
      desiredJobTitles: candidate.desiredJobTitles ?? [],
      clientBillRate: {
        currency: candidate?.clientBillRateCurrency ?? job?.salaryCurrency ?? null,
        value: candidate.clientBillRate ?? 0,
        timeSpan: candidate.clientBillRateTimeSpan ?? job?.salaryTimeSpan ?? null,
      },
      feeArrangementId: candidate?.feeArrangementId ?? job?.feeArrangementId,
      offeredPayRate: {
        currency: candidate?.offeredPayRateCurrency?.trim()
          ? candidate?.offeredPayRateCurrency
          : candidate?.expectedSalaryCurrency ?? null,
        value: candidate.offeredPayRate ?? 0,
        timeSpan: candidate.offeredPayRateTimeSpan ?? job?.salaryTimeSpan ?? null,
      },
      cv: 0,
      skillMatrixRatings: candidate?.skillMatrixRatings ?? [{ skill: '', rating: 0 }],
      portalId: candidate?.portalId ?? '',
      exEmployeeOfClient:
        candidate.exEmployeeOfClient != null
          ? candidate.exEmployeeOfClient
            ? { index: willingToTravelList['Yes'], key: 'Yes' }
            : { index: willingToTravelList['No'], key: 'No' }
          : null,
      exEmployeeId: candidate?.exEmployeeId ?? '',
      workAuthValidity: candidate?.workAuthValidity ?? null,
      candidateLanguages: candidate?.candidateLanguages ?? [],
      customNote: candidate?.customNote ?? '',
      otherInterviewStage: candidate?.otherInterviewStage ?? '',
      visaStatusOther: '',
      payRateExpenses: {
        currency: candidate?.payRateExpensesCurrency ?? candidate?.expectedSalaryCurrency ?? null,
        value: candidate?.payRateExpenses ?? 0,
        timeSpan: candidate?.payRateExpensesType ?? candidate.expectedBaseSalaryTimeSpan ?? null,
      },
    };
  } else {
    return {
      buId: BuContext?.length == 1 ? BuContext?.[0].id ?? '' : '',
      currentSalary: {
        currency: job?.salaryCurrency ?? null,
        value: 0,
        timeSpan: job?.salaryTimeSpan ?? null,
      },
      expectedSalary: {
        currency: job?.salaryCurrency ?? null,
        value: 0,
        timeSpan: job?.salaryTimeSpan ?? null,
      },
      clientBillRate: {
        currency: job?.salaryCurrency ?? null,
        value: 0,
        timeSpan: job?.salaryTimeSpan ?? null,
      },
      offeredPayRate: {
        currency: job?.salaryCurrency ?? null,
        value: 0,
        timeSpan: job?.salaryTimeSpan ?? null,
      },
      payRateExpenses: {
        currency: job?.salaryCurrency ?? null,
        value: 0,
        timeSpan: job?.salaryTimeSpan ?? null,
      },
      feeArrangementId: job?.feeArrangementId,
    };
  }
};

export const getBuId = (candidate: ICandidate | null, bu: IBusinessUnit[]) => {
  if (bu.length === 1) {
    return bu?.[0].id;
  } else {
    return candidate?.buId ?? '';
  }
};
